import React, { useState, useEffect, useMemo } from "react";
import axios from "axios";
import {
  baseUrl,
  timeList,
  rotiBatchSize,
  isRotliCategory,
} from "../comman/config";
import Select from "react-select";
import { ChevronUpIcon, ChevronDownIcon } from "@heroicons/react/solid";
import { DateSessionFilter } from "../components/Filter/DateSessionFilter";
import toast, { Toaster } from "react-hot-toast";
import Loader from "../components/Loader";
import { ItemNameStacked, itemDisplayName } from '../components/ItemName';

// A blank batch. `rotli_machine` and `kacho_mon_lot_ratio` are no longer columns on this screen,
// but they are still columns in rotli_production: the machine is carried through a load and a
// save untouched so a row keeps what it was given, and the ratio is still worked out from the two
// weights below. Dropping either from the payload would blank a stored value from a screen that
// no longer asks about it.
const blankBatch = (index) => ({
  batch: index + 1,
  rotli_machine: "",
  weight_of_kacho_lot: "",
  weight_of_mon_lot: "",
  gulla_weight: "",
  kacho_mon_lot_ratio: "",
  no_of_gulla: "",
  start_time: "",
  end_time: "",
  remarks: "",
});

const blankBatches = (count) => Array.from({ length: count }, (_, index) => blankBatch(index));

const num = (value) => {
  const parsed = parseFloat(value);
  return isNaN(parsed) ? null : parsed;
};

const sumOf = (rows, field) =>
  rows.reduce((total, row) => total + (num(row?.[field]) ?? 0), 0);

// Gulla weight is the weight of ONE ball, so the column totals to an average -- adding five ball
// weights together would be a number that means nothing. Rows left empty are not averaged in:
// a batch nobody has filled should not drag the figure towards zero.
const avgOf = (rows, field) => {
  const values = rows.map((row) => num(row?.[field])).filter((v) => v !== null);
  return values.length ? values.reduce((a, b) => a + b, 0) / values.length : 0;
};

// Two decimals at most, and none where there is nothing after the point: 41.6 rather than 41.60,
// 300 rather than 300.00.
const show = (value, decimals = 2) => {
  const parsed = num(value);
  if (parsed === null) return "";
  return String(Number(parsed.toFixed(decimals)));
};

// Anything typed into the batch at all. Save writes the batches someone has worked on and leaves
// the untouched ones alone -- otherwise pressing Save once would put an empty row in the table
// for every batch on screen.
const hasEntry = (row) =>
  ["weight_of_kacho_lot", "weight_of_mon_lot", "gulla_weight", "remarks"].some(
    (field) => String(row?.[field] ?? "") !== ""
  ) || !!row?.start_time?.value || !!row?.end_time?.value;

function RotliProductionList() {
  const today = new Date().toISOString().split("T")[0];
  // The day's whole menu as it came back. What the screen SHOWS is derived from it below, not
  // filtered on the way in: the filter submits itself on mount from the day it remembers, which
  // races the categories being read, and a list filtered once at that moment would keep whichever
  // answer won until somebody pressed Submit again.
  const [menu, setMenu] = useState([]);
  // Null until the categories have been read. Null and "read, and there is no Rotli category"
  // are not the same thing, and the filter below treats them differently.
  const [rotliCategoryIds, setRotliCategoryIds] = useState(null);
  const [activeIndex, setActiveIndex] = useState(null);
  const [date, setDate] = useState(today);
  const [session, setSession] = useState("");
  const [formData, setFormData] = useState([]);
  const [totalPlannedQty, setTotalPlannedQty] = useState(0);
  const [loading, setLoading] = useState(false)

  // Only the dishes in the Rotli category, which is what this screen is for. It used to ask
  // whether the item's method was 3, or its measurements mentioned 3 -- a method id written into
  // the screen, and one that no longer answers cleanly now that an item can hold more than one
  // method. The category is the question that was meant.
  //
  // The old test still stands in while the categories are unread -- a request that failed, or one
  // that has not come back yet -- so a dropped connection leaves the screen working rather than
  // empty, and the list corrects itself the moment the categories land.
  //
  // Memoised because two effects below take this list as a dependency, and a fresh array on every
  // render would set them running on every render.
  const mealList = useMemo(() => (
    rotliCategoryIds === null
      // String(): this now runs during render rather than inside the fetch, and an item with no
      // measurements recorded would take the screen down instead of failing quietly in a promise.
      ? menu.filter((item) => item.method == 3 || String(item.measurements ?? "").includes("3"))
      : menu.filter((item) => rotliCategoryIds.includes(String(item.menuitem_category)))
  ), [menu, rotliCategoryIds]);

  useEffect(() => {
    setFormData(blankBatches(rotiBatchSize));
    getRotliCategoryIds();
  }, []);

  const getRotliCategoryIds = async () => {
    try {
      const response = await axios.get(`${baseUrl}/comman/list?table=item_category_master&condition=status=1`);
      const ids = (response.data.data ?? []).filter(isRotliCategory).map((c) => String(c.id));
      setRotliCategoryIds(ids);
      if (ids.length === 0) {
        toast.error("No item category named Rotli was found -- check Item Category Master.");
      }
    } catch (error) {
      // Left null, and the day's menu is filtered the old way instead. A request that failed is
      // not the same as a master with no Rotli category in it, and emptying the screen over a
      // dropped connection would be the wrong answer to it.
      console.warn("[rotli] Could not read the item categories; falling back to the method filter.", error);
      setRotliCategoryIds(null);
    }
  };

  useEffect(() => {
    if (mealList.length > 0 && activeIndex != null) {
      getRMDetailForItem();
    }
  }, [mealList, activeIndex]);

  useEffect(() => {
    if (mealList.length > 0 && activeIndex != null) {
      const itemId =
        mealList.find((item, index) => index === activeIndex).menu_item_id ??
        null;
      const fetchDetail = async () => {
        setLoading(true)
        const condition = `menu_item_id='${itemId}' AND date = '${date}' AND session='${session.value}'`;
        const response = await axios.get(
          `${baseUrl}/comman/list?table=rotli_production&condition=${condition}`
        );
        const apiDetail = response.data.data;
        getDetailDispaly(apiDetail);
        setLoading(false)
      };
      fetchDetail();
    }
  }, [mealList, activeIndex]);

  const getDetailDispaly = async (detail) => {
    try {
      if (detail) {
        // Enough rows for everything already saved, and never fewer than the screen opens with.
        // A day that ran twelve batches must show twelve, not the opening five with the rest
        // hidden behind a button nobody knows to press.
        const highestSaved = detail.reduce((max, row) => Math.max(max, Number(row.batch) || 0), 0);
        const initialFormData = blankBatches(Math.max(rotiBatchSize, highestSaved));

        const updatedFormData = await Promise.all(initialFormData.map(async(initialItem) => {
          const matchingObject = detail.find(
            (object) => object.batch === initialItem.batch
          );

          if (matchingObject) {
            const start_time = matchingObject.start_time;
            const timeslotItem = timeList.find( (item) => item.value == start_time);
            const timeslotObject = timeslotItem
              ? { value: timeslotItem.value, label: timeslotItem.label }
              : { value: "", label: "" };

            const end_time = matchingObject.end_time;
            const endTimeItem = timeList.find((item) => item.value == end_time);
            const endTimeSlotObject = endTimeItem ?  { value: endTimeItem.value, label: endTimeItem.label } : { value: "", label: "" };

            // Read back in the shape the save endpoint expects and handed straight back to it,
            // so the machine a row was recorded against survives a save from this screen.
            matchingObject.rotli_machine = { value: matchingObject.rotli_machine ?? null };
            matchingObject.start_time = timeslotObject;
            matchingObject.end_time = endTimeSlotObject;

            return { ...initialItem,  ...matchingObject };
          }
           else {
          return {...initialItem}
          }
        }));
        setFormData(updatedFormData);
      } else {
        setFormData(blankBatches(rotiBatchSize));
      }
    } catch (e) {
      console.log(e);
    }
  };

  const handleClick = (index) => {
    setActiveIndex(index === activeIndex ? null : index);
  };

  const getRMDetailForItem = async () => {
    const item_id = mealList.find((item, index) => index == activeIndex);
    const condition = `date = '${date}' AND session = '${session.value}' AND menu_item_id = '${item_id.menu_item_id}'`;
    const response = await axios.get(
      `${baseUrl}/comman/list?table=production_planning&condition=${condition}`
    );
    const sum = response.data.data.reduce((total, obj) => {
      const qty = parseFloat(obj.planned_batch_raw_qty);
      return isNaN(qty) ? total : total + qty;
    }, 0);
    setTotalPlannedQty(sum);
  };

  const handelFilterChange = async (date, session) => {
    setDate(date);
    setSession(session);
    if (date && session) {
      setLoading(true)
      const condition = ` m.session='${session.value}' AND m.date='${date}'`;
      const response = await axios.get(
        `${baseUrl}/meal_master/item_list?condition=${condition}`
      );

      setMenu(response.data.data ?? []);
      setLoading(false)
    }
  };

  const handleInputChange = (index) => (e) => {
    const { name, value } = e.target;

      setFormData((prevFormData) => {
        const updatedFormData = [...prevFormData];

        updatedFormData[index] = { ...updatedFormData[index], [name]: value };

        const ratio = updatedFormData[index].weight_of_kacho_lot > 0 ? (updatedFormData[index].weight_of_mon_lot / updatedFormData[index].weight_of_kacho_lot).toFixed(3) : 0;
        const no_of_gulla = updatedFormData[index].gulla_weight > 0 ? ((updatedFormData[index].weight_of_mon_lot / updatedFormData[index].gulla_weight) * 1000).toFixed(3) : 0;
        updatedFormData[index] = { ...updatedFormData[index], ["kacho_mon_lot_ratio"]: ratio, ["no_of_gulla"] : no_of_gulla };
        return updatedFormData;
      });
  };

  const handleSelectChange = async (index, data, option) => {
      setFormData((prevFormData) => {
        const updatedFormData = [...prevFormData];
        updatedFormData[index] = {
          ...updatedFormData[index],
          [option.name]: data,
        };
        return updatedFormData;
      });
  };

  // One more batch on the end. The batch number is its position, which is what the save keys on,
  // so appending is the only way to add one -- there is no inserting in the middle.
  const handleAddBatch = () => {
    setFormData((prevFormData) => [...prevFormData, blankBatch(prevFormData.length)]);
  };

  const handleSaveAll = async (e) => {
    e.preventDefault();

    const menu_item_id =
      mealList.find((item, i) => i == activeIndex)?.menu_item_id ?? 0;
    const filled = formData.filter(hasEntry);
    if (!filled.length) {
      toast.error("Nothing entered to save yet.");
      return;
    }

    setLoading(true);
    try {
      // One request per batch, in order. The endpoint reads the batch's row before deciding
      // between an insert and an update, and it takes one batch at a time -- sending them
      // together would be a second way to write this table for no gain at these numbers.
      for (const row of filled) {
        await axios.post(`${baseUrl}/rotli_production/batch`, {
          formData: row,
          date: date,
          session: session,
          menu_item_id: menu_item_id,
        });
      }
      toast.success("Data successfully saved!");
    } catch (error) {
      toast.error("Failed to submit the form.");
      console.log(error);
    }
    setLoading(false);
  };

  const totalActualRM = sumOf(formData, "weight_of_kacho_lot");
  const totalMonLot = sumOf(formData, "weight_of_mon_lot");
  const avgGullaWeight = avgOf(formData, "gulla_weight");
  const totalGulla = sumOf(formData, "no_of_gulla");
  // What is left to do, not the difference in whichever direction it happens to fall: planned
  // less what has actually gone in. This read the other way round and reported the pending
  // quantity as a negative number all the way through a batch.
  const pendingRM = totalPlannedQty - totalActualRM;

  // Narrow enough that the five columns that matter -- batch, the two flour weights, the gulla
  // weight and the count they give -- are all on a phone screen without scrolling. Start time,
  // End time and Remarks sit past the edge and are scrolled to.
  const numCell = "border border-gray-300 p-1 w-[4.5rem]";
  // The three weights the batch is actually entered from -- કાચો લોટ, મોણવાળો લોટ and ગુલ્લા --
  // get the room on a desk, where their headings were wrapping onto four and five lines in
  // 4.5rem with the rest of the screen empty beside them. The phone width is left where it is:
  // it is what keeps those three and the count they produce on one screen without scrolling,
  // which is the note above.
  const wideNumCell = "border border-gray-300 p-1 w-[4.5rem] sm:w-32";
  const inputCell = "w-full p-1 border rounded-md text-xs bg-orange-50";

  return (
    // The page's left edge, owned in one place. The heading carried it (px-2 sm:px-6) and
    // nothing under it did, so on a desk the title sat 24px in, the filter 8px in, and the item
    // bars hard against the window -- three left edges down one screen.
    <div className="px-2 sm:px-6">
      {loading && <Loader/>}
      <div className="flex justify-between items-center py-4">
        <h1 className="text-lg sm:text-xl md:text-xl lg:text-xl xl:text-2xl font-bold">
          Rotli Production
        </h1>
        <Toaster position="top-center" reverseOrder={false} />
      </div>
      <div className="relative  shadow-md sm:rounded-lg">
        {/* Date, session and Submit on one line -- this screen is worked on a phone, where the
            roomier default layout puts them on three.
            -mx-2 cancels the compact filter's own px-2 so its date box starts on the same edge as
            the heading above and the item bars below. The filter is shared with Production
            Support, so the padding comes off here rather than out of it. */}
        <div className="-mx-2">
          <DateSessionFilter handleFilterChange={handelFilterChange} compact />
        </div>
        <div>
          {mealList.map((item, index) => (
            <div key={index} className="border-b mt-4">
              <div
                className="flex justify-between items-center p-4 cursor-pointer accordion-title bg-orange-400 border rounded"
                onClick={() => handleClick(index)}
              >
                <span><ItemNameStacked item={item} /></span>
                {index === activeIndex ? (
                  <ChevronUpIcon className="w-6 h-6" />
                ) : (
                  <ChevronDownIcon className="w-6 h-6" />
                )}
              </div>
              {index === activeIndex && (
                <div className="space-y-3 p-2 sm:p-4 bg-white">
                  {/* The day's standing at a glance: what was planned, what has gone in, what is
                      left, and what it has produced. */}
                  <div className="overflow-x-auto">
                    <table className="text-xs sm:text-sm text-center border-collapse">
                      <thead>
                        <tr className="bg-gray-100">
                          <th className="border border-gray-300 p-2 font-bold">Planned RM<br />(kgs)</th>
                          <th className="border border-gray-300 p-2 font-bold">Actual RM<br />(kgs)</th>
                          <th className="border border-gray-300 p-2 font-bold">Pending RM<br />(kgs)</th>
                          <th className="border border-gray-300 p-2 font-bold">
                            કુલ {itemDisplayName(item)} (નંગ)
                          </th>
                        </tr>
                      </thead>
                      <tbody>
                        <tr>
                          <td className="border border-gray-300 p-2">{show(totalPlannedQty)}</td>
                          <td className="border border-gray-300 p-2">{show(totalActualRM)}</td>
                          <td className="border border-gray-300 p-2">{show(pendingRM)}</td>
                          <td className="border border-gray-300 p-2">{show(totalGulla, 0)}</td>
                        </tr>
                      </tbody>
                    </table>
                  </div>

                  <div className="overflow-x-auto">
                    <table className="text-xs text-center border-collapse">
                      <thead>
                        <tr className="bg-gray-100 align-bottom">
                          <th className="border border-gray-300 p-1 w-12 font-bold">બેચ<br />(નંબર)</th>
                          <th className={`${wideNumCell} font-bold`}>કાચો લોટનું વજન (કિલો)</th>
                          <th className={`${wideNumCell} font-bold`}>મોણવાળો લોટ નું વજન (કિલો)</th>
                          <th className={`${wideNumCell} font-bold`}>ગુલ્લા નું વજન (ગ્રામ)</th>
                          <th className={`${numCell} font-bold`}>આશરે ગુલ્લા (નંગ)</th>
                          <th className="border border-gray-300 p-1 w-32 font-bold">Start time</th>
                          <th className="border border-gray-300 p-1 w-32 font-bold">End Time</th>
                          <th className="border border-gray-300 p-1 w-40 font-bold">નોંધ / રીમાર્કસ</th>
                        </tr>
                      </thead>
                      <tbody>
                        {formData.map((row, batchIndex) => (
                          <tr key={batchIndex}>
                            <td className="border border-gray-300 p-1 font-medium">{batchIndex + 1}</td>
                            <td className={wideNumCell}>
                              <input
                                type="number"
                                step="0.01"
                                id={`weight_of_kacho_lot_${batchIndex}`}
                                name="weight_of_kacho_lot"
                                value={row?.weight_of_kacho_lot ?? ""}
                                onChange={handleInputChange(batchIndex)}
                                onWheel={(e) => e.target.blur()}
                                onKeyDown={(e) => {if (e.key === 'ArrowUp' || e.key === 'ArrowDown') { e.preventDefault(); }}}
                                className={inputCell}
                              />
                            </td>
                            <td className={wideNumCell}>
                              <input
                                type="number"
                                step="0.01"
                                id={`weight_of_mon_lot_${batchIndex}`}
                                name="weight_of_mon_lot"
                                value={row?.weight_of_mon_lot ?? ""}
                                onChange={handleInputChange(batchIndex)}
                                onWheel={(e) => e.target.blur()}
                                onKeyDown={(e) => {if (e.key === 'ArrowUp' || e.key === 'ArrowDown') { e.preventDefault(); }}}
                                className={inputCell}
                              />
                            </td>
                            <td className={wideNumCell}>
                              <input
                                type="number"
                                step="0.01"
                                id={`gulla_weight_${batchIndex}`}
                                name="gulla_weight"
                                value={row?.gulla_weight ?? ""}
                                onChange={handleInputChange(batchIndex)}
                                onWheel={(e) => e.target.blur()}
                                onKeyDown={(e) => {if (e.key === 'ArrowUp' || e.key === 'ArrowDown') { e.preventDefault(); }}}
                                className={inputCell}
                              />
                            </td>
                            {/* Worked out from the two beside it, so it is read rather than
                                typed -- and read as a whole number, being a count of gullas. */}
                            <td className={`${numCell} bg-gray-50`}>{show(row?.no_of_gulla, 0)}</td>
                            <td className="border border-gray-300 p-1 w-32">
                              <Select
                                classNamePrefix="select"
                                name="start_time"
                                menuPortalTarget={document.body}
                                styles={{
                                  menuPortal: (base) => ({ ...base, zIndex: 9999 }),
                                  placeholder: (provided) => ({ ...provided, fontSize: "0.75rem" }),
                                  control: (provided) => ({ ...provided, backgroundColor: '#FFFAF0', fontSize: '14px', minHeight: '32px' }),
                                }}
                                value={row?.start_time}
                                onChange={(selectedOption, name) => {
                                  handleSelectChange(batchIndex, selectedOption, name);
                                }}
                                placeholder="Start Time"
                                options={timeList.map((item) => ({
                                  value: item.value,
                                  label: item.label,
                                }))}
                                filterOption={(option, inputValue) => {
                                  return option.data.label
                                    .toLowerCase()
                                    .includes(inputValue.toLowerCase());
                                }}
                              />
                            </td>
                            <td className="border border-gray-300 p-1 w-32">
                              <Select
                                classNamePrefix="select"
                                name="end_time"
                                menuPortalTarget={document.body}
                                styles={{
                                  menuPortal: (base) => ({ ...base, zIndex: 9999 }),
                                  placeholder: (provided) => ({ ...provided, fontSize: "0.75rem" }),
                                  control: (provided) => ({ ...provided, backgroundColor: '#FFFAF0', fontSize: '14px', minHeight: '32px' }),
                                }}
                                value={row?.end_time}
                                onChange={(selectedOption, name) => {
                                  handleSelectChange(batchIndex, selectedOption, name);
                                }}
                                placeholder="End Time"
                                options={timeList.map((item) => ({
                                  value: item.value,
                                  label: item.label,
                                }))}
                                filterOption={(option, inputValue) => {
                                  return option.data.label
                                    .toLowerCase()
                                    .includes(inputValue.toLowerCase());
                                }}
                              />
                            </td>
                            <td className="border border-gray-300 p-1 w-40">
                              <input
                                type="text"
                                placeholder="Remarks"
                                id={`remarks_${batchIndex}`}
                                name="remarks"
                                value={row?.remarks ?? ""}
                                onChange={handleInputChange(batchIndex)}
                                className={inputCell}
                              />
                            </td>
                          </tr>
                        ))}
                        <tr className="bg-gray-100 font-bold">
                          <td className="border border-gray-300 p-1">TOTAL</td>
                          <td className={numCell}>{show(totalActualRM)}</td>
                          <td className={numCell}>{show(totalMonLot)}</td>
                          {/* An average, not a total -- see avgOf. */}
                          <td className={numCell}>{show(avgGullaWeight)}</td>
                          <td className={numCell}>{show(totalGulla, 0)}</td>
                          <td className="border border-gray-300 p-1"></td>
                          <td className="border border-gray-300 p-1"></td>
                          <td className="border border-gray-300 p-1"></td>
                        </tr>
                      </tbody>
                    </table>
                  </div>

                  <div className="flex items-center gap-4 pt-1">
                    <button
                      onClick={handleAddBatch}
                      className="px-4 py-2 bg-blue-500 text-white rounded-md text-sm hover:bg-blue-600 focus:outline-none focus:ring focus:ring-blue-200"
                    >
                      Add Batch
                    </button>
                    <button
                      onClick={handleSaveAll}
                      className="px-4 py-2 bg-green-500 text-white rounded-md text-sm hover:bg-green-600 focus:outline-none focus:ring focus:ring-green-200"
                    >
                      SAVE
                    </button>
                  </div>
                </div>
              )}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

export default RotliProductionList;
